/**
 * Static, client-side registry of supported time-tracker providers.
 * Kimai is the only entry today; every consumer (provider picker, connect dialog,
 * the section's logo/chip, the disconnect confirm's copy, the Stop Timer dialog's
 * title) reads from this registry rather than hardcoding a provider name, so adding
 * a second provider later is a registry entry, not new UI.
 */
const PROVIDERS = {
  kimai: {
    id: 'kimai',
    name: 'Kimai',
    // Icon *name*, not a template: the artwork lives with the rest of the app's icons
    // in `src/components/icons/time-tracking-provider.js`, and each component that
    // renders it registers that module with `DwIcon.addIcons()`. Keeps a large brand
    // asset out of the Redux layer, and out of the bundle for anyone who never renders it.
    logo: 'kimai',
    taglineKey: 'time-tracker:providers.kimai.tagline',
    // Field descriptor rendered by both time-tracker-connect-dialog (admin, all fields)
    // and time-tracker-user-connect-dialog (member, apiKey only).
    connectFields: [
      {
        name: 'host',
        labelKey: 'time-tracker:providers.kimai.fields.host.label',
        placeholderKey: 'time-tracker:providers.kimai.fields.host.placeholder',
        adminOnly: true,
      },
      {
        name: 'apiKey',
        labelKey: 'time-tracker:providers.kimai.fields.apiKey.label',
        placeholderKey: 'time-tracker:providers.kimai.fields.apiKey.placeholder',
      },
    ],
    // The "how to obtain an API key" video is deliberately *not* here: its url comes
    // from the app config (`timeTracker.helpVideoUrl.kimai`), so Support can publish or
    // swap it without a release. See `selectors.helpVideoUrl`.
  },
};

/**
 * @returns {Array} every registry entry, for the provider picker. Static client-side
 * data - takes no accountId/state.
 */
export const list = () => Object.values(PROVIDERS);

/**
 * Case-insensitive: the registry is keyed in lower case, but the server stores and
 * returns the provider as its own enum value - `KIMAI`, not `kimai`. Looking that up
 * verbatim found nothing, so the provider resolved as undefined and every name, logo and
 * `{{provider}}` interpolation came out empty on a perfectly healthy connection.
 *
 * @param {String} id provider id, in any case - e.g. 'kimai' or 'KIMAI'.
 * @returns {Object|undefined} the registry entry.
 */
export const get = (id) => (id ? PROVIDERS[String(id).toLowerCase()] : undefined);
